import { Check, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export interface SelectOption {
  value: string;
  label: string;
}

export default function StyledSelect({
  label,
  value,
  options,
  placeholder,
  disabled,
  required,
  searchable = false,
  searchPlaceholder,
  emptyMessage,
  onChange,
}: {
  label: string;
  value: string;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const root = useRef<HTMLDivElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const selected = options.find((option) => option.value === value);
  const normalize = (text: string) => text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase();
  const filteredOptions = searchable && search.trim()
    ? options.filter(option => normalize(option.label).includes(normalize(search.trim())))
    : options;

  useEffect(() => {
    if (open && searchable) searchInput.current?.focus();
    if (!open) setSearch("");
  }, [open, searchable]);

  useEffect(() => {
    const close = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);

  function move(direction: number) {
    if (!options.length) return;
    const current = Math.max(
      0,
      options.findIndex((option) => option.value === value),
    );
    const next = (current + direction + options.length) % options.length;
    onChange(options[next].value);
  }

  return (
    <div className={"styled-select" + (open ? " open" : "")} ref={root}>
      <select
        className="styled-select-native"
        aria-label={label}
        tabIndex={-1}
        required={required}
        disabled={disabled}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="" disabled />
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="styled-select-trigger"
        aria-label={label + " options"}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            if (!open) setOpen(true);
            else if (searchable) searchInput.current?.focus();
            else move(event.key === "ArrowDown" ? 1 : -1);
          } else if (event.key === "Escape") {
            setOpen(false);
          }
        }}
      >
        <span className={selected ? "" : "placeholder"}>
          {selected?.label || placeholder}
        </span>
        <ChevronDown size={16} />
      </button>
      {open && !disabled && (
        <div className="styled-select-menu" role={searchable ? undefined : "listbox"} aria-label={searchable ? undefined : label}>
          {searchable && <div className="styled-select-search">
            <input
              ref={searchInput}
              type="search"
              value={search}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder || label}
              onChange={event => setSearch(event.target.value)}
              onKeyDown={event => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  if (filteredOptions.length === 1) { onChange(filteredOptions[0].value); setOpen(false); }
                } else if (event.key === "ArrowDown") {
                  event.preventDefault();
                  root.current?.querySelector<HTMLButtonElement>('[role="option"]')?.focus();
                } else if (event.key === "Escape") {
                  event.preventDefault();
                  setOpen(false);
                  root.current?.querySelector<HTMLButtonElement>(".styled-select-trigger")?.focus();
                }
              }}
            />
          </div>}
          <div role={searchable ? "listbox" : undefined} aria-label={searchable ? label : undefined}>
          {filteredOptions.map((option) => (
            <button
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={option.value === value ? "selected" : ""}
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              <span>{option.label}</span>
              {option.value === value && <Check size={15} />}
            </button>
          ))}
          {searchable && !filteredOptions.length && <p className="styled-select-empty" role="status">{emptyMessage}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
